import { useEffect, useState } from 'react';
import { Pagination } from '@mui/material';
import { Chip, Button } from '@coordio/ui';
import { toolService } from '../../services/toolService';
import type { ToolExecutionLogDto } from '../../types/tool';

export interface ToolExecutionHistoryProps {
  toolId: string;
}

/**
 * Displays a tool's execution history.
 */
export const ToolExecutionHistory = ({ toolId }: ToolExecutionHistoryProps) => {
  const [items, setItems] = useState<ToolExecutionLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await toolService.getToolExecutions(toolId, pageNumber, pageSize);
      setItems(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load execution history');
    } finally {
      setLoading(false);
    }
  };

  const download = (filename: string, content: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJson = () => {
    download(`tool-${toolId}-executions.json`, JSON.stringify(items, null, 2), 'application/json');
  };

  const exportCsv = () => {
    const header = [
      'id',
      'executedAt',
      'success',
      'isDryRun',
      'executionTimeMs',
      'toolName',
      'toolKey',
      'errorMessage',
    ];

    const escape = (value: unknown) => {
      const s = value === null || value === undefined ? '' : String(value);
      return `"${s.replaceAll('"', '""')}"`;
    };

    const rows = items.map((e) =>
      [
        e.id,
        e.executedAt,
        e.success,
        e.isDryRun,
        e.executionTimeMs,
        e.toolName,
        e.toolKey,
        e.errorMessage || '',
      ].map(escape).join(',')
    );

    download(`tool-${toolId}-executions.csv`, [header.join(','), ...rows].join('\n'), 'text/csv');
  };

  useEffect(() => {
    if (toolId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId, pageNumber]);

  if (loading) {
    return (
      <div className="flex justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:border-red-800 dark:text-red-400 flex items-center justify-between">
        <span>{error}</span>
        <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">✕</button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">
          No executions recorded yet.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium">Executions</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={exportJson} disabled={items.length === 0}>
            Export JSON
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={items.length === 0}>
            Export CSV
          </Button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Executed At</th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Dry-run</th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Time (ms)</th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Error</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id} className="border-b border-border hover:bg-muted/50 transition-colors">
              <td className="px-3 py-2">{new Date(e.executedAt).toLocaleString()}</td>
              <td className="px-3 py-2">
                <Chip
                  label={e.success ? 'Success' : 'Failed'}
                  color={e.success ? 'green' : 'red'}
                  variant="outlined"
                />
              </td>
              <td className="px-3 py-2">
                <Chip
                  label={e.isDryRun ? 'Yes' : 'No'}
                  color={e.isDryRun ? 'amber' : 'default'}
                  variant="outlined"
                />
              </td>
              <td className="px-3 py-2">{e.executionTimeMs}</td>
              <td className="px-3 py-2">
                <span className={`text-xs ${e.success ? 'text-muted-foreground' : 'text-red-500'}`}>
                  {e.errorMessage || '-'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {totalCount > pageSize && (
        <div className="flex justify-center mt-4">
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={pageNumber}
            onChange={(_, p) => setPageNumber(p)}
            color="primary"
          />
        </div>
      )}
    </div>
  );
};
